/** Appels d'API regroupés par domaine. */

import { api, type BinaryResponse } from "./client";
import type {
  Archive,
  AuditEntry,
  Business,
  BusinessOptions,
  Dashboard,
  DocumentItem,
  ExpectedEventAnswerResult,
  ExpectedEventOutcome,
  GenerateTasksResult,
  Health,
  MessageResponse,
  NotificationItem,
  NotificationPreferences,
  Page,
  RecoveryCodes,
  RestoreMode,
  RestorePreview,
  RestoreResult,
  RevenueDeclaration,
  RuleLibrary,
  SessionInfo,
  SessionState,
  TaskDetail,
  TaskHistoryItem,
  TaskListItem,
  TaskOptions,
  TaskStatus,
  ThresholdWatch,
  TotpSetup,
} from "./types";

export const authApi = {
  register: (body: { email: string; password: string; display_name?: string }) =>
    api.post<MessageResponse>("/auth/register", body),
  verifyEmail: (token: string) =>
    api.post<MessageResponse>("/auth/verify-email", { token }),
  resendVerification: (email: string) =>
    api.post<MessageResponse>("/auth/resend-verification", { email }),
  login: (body: { email: string; password: string }) =>
    api.post<SessionState>("/auth/login", body),
  logout: () => api.post<MessageResponse>("/auth/logout"),
  session: () => api.get<SessionState>("/auth/session"),
  sessions: () => api.get<SessionInfo[]>("/auth/sessions"),
  revokeSession: (id: string) =>
    api.delete<MessageResponse>(`/auth/sessions/${id}`),
  revokeOtherSessions: () =>
    api.delete<MessageResponse>("/auth/sessions/others"),
  sendTwoFactorCode: () =>
    api.post<MessageResponse>("/auth/2fa/send-code", { method: "email" }),
  verifyTwoFactor: (body: { method: string; code: string }) =>
    api.post<SessionState>("/auth/2fa/verify", body),
  totpSetup: () => api.post<TotpSetup>("/auth/2fa/totp/setup"),
  totpConfirm: (code: string) =>
    api.post<RecoveryCodes>("/auth/2fa/totp/confirm", { code }),
  totpDisable: () => api.post<MessageResponse>("/auth/2fa/totp/disable"),
  emailTwoFactorEnable: () =>
    api.post<RecoveryCodes | MessageResponse>("/auth/2fa/email/enable"),
  emailTwoFactorDisable: () =>
    api.post<MessageResponse>("/auth/2fa/email/disable"),
  regenerateRecoveryCodes: () =>
    api.post<RecoveryCodes>("/auth/2fa/recovery-codes"),
  reauthenticate: (body: {
    password: string;
    twofa_code?: string;
    twofa_method?: string;
  }) => api.post<MessageResponse>("/auth/reauthenticate", body),
  changePassword: (body: { current_password: string; new_password: string }) =>
    api.post<MessageResponse>("/auth/password", body),
  changeEmail: (new_email: string) =>
    api.post<MessageResponse>("/auth/email", { new_email }),
  requestPasswordReset: (email: string) =>
    api.post<MessageResponse>("/auth/password-reset", { email }),
  confirmPasswordReset: (body: { token: string; new_password: string }) =>
    api.post<MessageResponse>("/auth/password-reset/confirm", body),
  deleteAccount: (confirmation: string) =>
    api.post<MessageResponse>("/auth/delete-account", { confirmation }),
};

export const businessApi = {
  options: () => api.get<BusinessOptions>("/businesses/options"),
  create: (body: Record<string, unknown>) =>
    api.post<Business>("/businesses", body),
  current: () => api.get<Business>("/businesses/current"),
  update: (body: Record<string, unknown>) =>
    api.patch<Business>("/businesses/current", body),
  generateTasks: () =>
    api.post<GenerateTasksResult>("/businesses/current/generate-tasks"),
  completeOnboarding: () =>
    api.post<MessageResponse>("/businesses/current/complete-onboarding"),
  rulesState: () => api.get<MessageResponse>("/businesses/current/rules-state"),
  vatThresholds: () =>
    api.get<ThresholdWatch>("/businesses/current/vat-thresholds"),
  revenues: () =>
    api.get<RevenueDeclaration[]>("/businesses/current/revenues"),
  upsertRevenue: (body: Record<string, unknown>) =>
    api.put<RevenueDeclaration>("/businesses/current/revenues", body),
};

export interface TaskQuery {
  search?: string;
  status?: TaskStatus[];
  obligation?: string[];
  category?: string[];
  priority_level?: string[];
  authority?: string;
  source?: string;
  due_before?: string;
  due_after?: string;
  horizon_days?: number;
  include_archived?: boolean;
  include_trashed?: boolean;
  only_archived?: boolean;
  only_trashed?: boolean;
  only_missing_information?: boolean;
  only_needs_verification?: boolean;
  only_expected_events?: boolean;
  with_payment?: boolean;
  sort?: string;
  direction?: string;
  limit?: number;
  offset?: number;
}

export const tasksApi = {
  options: () => api.get<TaskOptions>("/tasks/options"),
  list: (query: TaskQuery = {}) =>
    api.get<Page<TaskListItem>>("/tasks", {
      query: query as Record<string, string | number | boolean | string[] | undefined>,
    }),
  get: (id: string) => api.get<TaskDetail>(`/tasks/${id}`),
  create: (body: Record<string, unknown>) => api.post<TaskDetail>("/tasks", body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<TaskDetail>(`/tasks/${id}`, body),
  setStatus: (
    id: string,
    body: {
      status: TaskStatus;
      note?: string;
      reference?: string;
      waiting_reason?: string;
    },
  ) => api.put<TaskDetail>(`/tasks/${id}/status`, body),
  /** Réponse à un événement attendu (« Avez-vous reçu… ? »). */
  answerExpectedEvent: (
    id: string,
    body: { outcome: ExpectedEventOutcome; note?: string },
  ) => api.post<ExpectedEventAnswerResult>(`/tasks/${id}/expected-event`, body),
  history: (id: string) => api.get<TaskHistoryItem[]>(`/tasks/${id}/history`),
  archive: (id: string, reason?: string) =>
    api.post<TaskDetail>(`/tasks/${id}/archive`, { reason }),
  unarchive: (id: string) => api.post<TaskDetail>(`/tasks/${id}/unarchive`),
  trash: (id: string, reason?: string) =>
    api.delete<MessageResponse>(`/tasks/${id}`, { query: { reason } }),
  restore: (id: string) => api.post<TaskDetail>(`/tasks/${id}/restore`),
  deletePermanently: (id: string) =>
    api.delete<MessageResponse>(`/tasks/${id}/permanent`),
  addDependency: (id: string, dependsOn: string, kind = "blocks") =>
    api.post<TaskDetail>(`/tasks/${id}/dependencies`, {
      depends_on_task_id: dependsOn,
      kind,
    }),
  removeDependency: (id: string, otherId: string) =>
    api.delete<TaskDetail>(`/tasks/${id}/dependencies/${otherId}`),
  recomputePriorities: () =>
    api.post<MessageResponse>("/tasks/recompute-priorities"),
  authorities: () => api.get<string[]>("/tasks/meta/authorities"),
};

export const dashboardApi = {
  get: () => api.get<Dashboard>("/dashboard"),
};

export const documentsApi = {
  list: (query: { task_id?: string; search?: string; include_trashed?: boolean; limit?: number; offset?: number } = {}) =>
    api.get<Page<DocumentItem>>("/documents", { query }),
  get: (id: string) => api.get<DocumentItem>(`/documents/${id}`),
  upload: (file: File, extra: { task_id?: string; display_name?: string; doc_kind?: string; notes?: string } = {}) => {
    const form = new FormData();
    form.append("file", file);
    for (const [key, value] of Object.entries(extra)) {
      if (value) form.append(key, value);
    }
    return api.upload<DocumentItem>("/documents", form);
  },
  download: (id: string): Promise<BinaryResponse> =>
    api.download(`/documents/${id}/download`),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<DocumentItem>(`/documents/${id}`, body),
  link: (id: string, taskId: string) =>
    api.post<DocumentItem>(`/documents/${id}/link`, { task_id: taskId }),
  unlink: (id: string, taskId: string) =>
    api.delete<MessageResponse>(`/documents/${id}/link/${taskId}`),
  trash: (id: string) => api.delete<MessageResponse>(`/documents/${id}`),
  restore: (id: string) => api.post<DocumentItem>(`/documents/${id}/restore`),
  deletePermanently: (id: string) =>
    api.delete<MessageResponse>(`/documents/${id}/permanent`),
};

export const notificationsApi = {
  list: (query: { unread_only?: boolean; limit?: number; offset?: number } = {}) =>
    api.get<Page<NotificationItem>>("/notifications", { query }),
  unreadCount: () => api.get<MessageResponse>("/notifications/unread-count"),
  markRead: (id: string) =>
    api.post<NotificationItem>(`/notifications/${id}/read`),
  markAllRead: () => api.post<MessageResponse>("/notifications/read-all"),
  dismiss: (id: string) => api.delete<MessageResponse>(`/notifications/${id}`),
  preferences: () =>
    api.get<NotificationPreferences>("/notifications/preferences"),
  updatePreferences: (body: Partial<NotificationPreferences>) =>
    api.patch<NotificationPreferences>("/notifications/preferences", body),
  runReminders: () =>
    api.post<{
      created_notifications: number;
      emails_sent: number;
      emails_failed: number;
      skipped_duplicates: number;
      channel_hint: string;
    }>("/notifications/run-reminders"),
};

export const dataApi = {
  createBackup: (body: { include_documents: boolean; include_audit: boolean }) =>
    api.post<Archive>("/data/backups", body),
  export: (query: { include_documents?: boolean; include_audit?: boolean } = {}) =>
    api.download("/data/export", { query }),
  archives: () => api.get<Page<Archive>>("/data/archives"),
  downloadArchive: (id: string) =>
    api.download(`/data/archives/${id}/download`),
  deleteArchive: (id: string) =>
    api.delete<MessageResponse>(`/data/archives/${id}`),
  restorePreview: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<RestorePreview>("/data/restore/preview", form);
  },
  restore: (file: File, mode: RestoreMode, confirmation: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("mode", mode);
    form.append("confirmation", confirmation);
    return api.upload<RestoreResult>("/data/restore", form);
  },
  restoreHistory: () => api.get<Record<string, unknown>[]>("/data/restore/history"),
  format: () => api.get<MessageResponse>("/data/format"),
};

export const miscApi = {
  audit: (query: { limit?: number; offset?: number; action?: string } = {}) =>
    api.get<Page<AuditEntry>>("/audit", { query }),
  accountAudit: (query: { limit?: number; offset?: number } = {}) =>
    api.get<Page<AuditEntry>>("/audit/account", { query }),
  history: (query: { limit?: number; offset?: number } = {}) =>
    api.get<Page<TaskHistoryItem>>("/history", { query }),
  rules: (query: { only_applicable?: boolean } = {}) =>
    api.get<RuleLibrary>("/rules", { query }),
  rule: (key: string) => api.get<Record<string, unknown>>(`/rules/${key}`),
  syncRules: () => api.post<MessageResponse>("/rules/sync"),
  settings: () => api.get<SessionState["user"]>("/settings/me"),
  updateSettings: (body: Record<string, unknown>) =>
    api.patch<SessionState["user"]>("/settings/me", body),
  health: () => api.get<Health>("/health"),
};
